import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, ArrowUp } from 'lucide-react';

const footerLinks = {
  services: [
    { label: 'Mobil Uygulama', path: '/hizmetler' },
    { label: 'Kurumsal Otomasyon', path: '/hizmetler' },
    { label: 'Bulut Çözümleri', path: '/hizmetler' },
    { label: 'SEO & Dijital Pazarlama', path: '/hizmetler' },
    { label: 'Siber Güvenlik', path: '/hizmetler' },
  ],
  company: [
    { label: 'Hakkımızda', path: '/hakkimizda' },
    { label: 'Referanslar', path: '/referanslar' },
    { label: 'İletişim', path: '/iletisim' },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/in/can-gevez-11222632a/', label: 'LinkedIn' },
  { icon: Instagram, href: 'https://instagram.com/gevezsoftware', label: 'Instagram' },
];

export const Footer = () => {
  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-background-primary border-t border-white/5" data-testid="footer">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link
              to="/"
              className="flex items-center gap-2 mb-6 group"
            >
              <div className="w-10 h-10 flex items-center justify-center relative">
                <svg viewBox="0 0 40 40" className="w-full h-full">
                  <polygon
                    points="20,2 38,11 38,29 20,38 2,29 2,11"
                    fill="none"
                    stroke="#00E5FF"
                    strokeWidth="2"
                    className="group-hover:stroke-brand-purple transition-colors duration-300"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="6"
                    fill="#00E5FF"
                    className="group-hover:fill-brand-purple transition-colors duration-300"
                  />
                </svg>
              </div>
              <span className="font-display text-xl font-bold text-white">
                GEVEZ SOFTWARE
              </span>
            </Link>
            
            <p className="font-mono text-text-secondary text-sm leading-relaxed mb-6">
              Modern, güvenli ve ölçeklenebilir yazılım çözümleri. Geleceği birlikte kodluyoruz.
            </p>

            {/* Social links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-text-secondary 
                               hover:bg-brand-cyan/20 hover:text-brand-cyan transition-all duration-300"
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services links */}
          <div>
            <h4 className="font-display text-white font-bold mb-6">Hizmetler</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="font-mono text-sm text-text-secondary hover:text-brand-cyan transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h4 className="font-display text-white font-bold mb-6">Şirket</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="font-mono text-sm text-text-secondary hover:text-brand-cyan transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter / Contact */}
          <div>
            <h4 className="font-display text-white font-bold mb-6">Bülten</h4>
            <p className="font-mono text-sm text-text-secondary mb-4">
              Güncel haberler ve yeniliklerden haberdar olun.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="E-posta adresiniz"
                className="flex-1 bg-white/5 border border-white/10 px-4 py-2 text-sm text-white font-mono
                           focus:outline-none focus:border-brand-cyan transition-colors rounded-none"
                data-testid="newsletter-input"
              />
              <button
                className="px-4 py-2 bg-brand-cyan text-background-primary font-display font-bold text-sm
                           hover:bg-white transition-colors"
                data-testid="newsletter-submit"
              >
                Abone
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-text-muted text-center md:text-left">
            © {new Date().getFullYear()} GEVEZ SOFTWARE. Tüm hakları saklıdır.
          </p>

          {/* Back to top */}
          <motion.button
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="flex items-center gap-2 text-text-secondary hover:text-brand-cyan transition-colors"
            data-testid="back-to-top"
          >
            <span className="font-mono text-xs">Yukarı</span>
            <ArrowUp size={16} />
          </motion.button>
        </div>
      </div>

      {/* Decorative gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-cyan/30 to-transparent" />
    </footer>
  );
};

export default Footer;
